import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Button,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from './AuthContext'; // Adjust the import path as necessary
import API from './services/api';
import { getAppBranding } from './config/appBranding';
import { hasAllModuleAccessEmail, hasExplicitFireAccessEmail } from './utils/fireAccess';
import { hasCommitteeMeetingAccess } from './utils/committeeAccess';
import { hasMaintenanceLogAccessEmail } from './utils/maintenanceAccess';
import { hasWcbDashboardAccessEmail } from './utils/wcbAccess';
import { getAppPath } from './utils/runtimeMode';
import { getNormalizedUserEmail } from './utils/userIdentity';

const Header = () => {
  const { isAuthenticated, logout, user } = useAuth();
  // Get allowedPages with a fallback empty array
  const { allowedPages = [] } = useAuth();
  const branding = getAppBranding();

  const [profileJobTitle, setProfileJobTitle] = useState('');
  const [profileDepartmentId, setProfileDepartmentId] = useState('');
  const [profileDepartmentName, setProfileDepartmentName] = useState('');
  const [profileDepartmentIds, setProfileDepartmentIds] = useState([]);
  const [profileDepartments, setProfileDepartments] = useState([]);
  const [profileEmail, setProfileEmail] = useState('');
  const [profileHasFireDepartmentAccess, setProfileHasFireDepartmentAccess] = useState(false);
  const [profileHasNonFireDepartmentAccess, setProfileHasNonFireDepartmentAccess] = useState(false);
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const avatarButtonRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loadProfile = async () => {
      if (!isAuthenticated) {
        setProfileJobTitle('');
        setProfileDepartmentId('');
        setProfileDepartmentName('');
        setProfileDepartmentIds([]);
        setProfileDepartments([]);
        setProfileEmail('');
        setProfileHasFireDepartmentAccess(false);
        setProfileHasNonFireDepartmentAccess(false);
        return;
      }
      try {
        const resp = await API.get('/api/users/me');
        const profile = resp?.data?.user || resp?.data || {};
        const title = String(profile.jobTitle || profile.TITLE || profile.title || '').trim();
        const deptIdRaw = profile.departmentId ?? profile.DEPARTMENT_ID ?? profile.department_id ?? profile.DEPARTMENTID;
        const deptNameRaw = profile.department || profile.DEPARTMENT || profile.departmentName || profile.DEPARTMENT_NAME || '';
        const deptIdsRaw = profile.departmentIds || profile.DEPARTMENT_IDS || [];
        const deptsRaw = profile.departments || profile.DEPARTMENTS || [];
        const deptId = deptIdRaw === undefined || deptIdRaw === null ? '' : String(deptIdRaw).trim();
        const deptName = String(deptNameRaw || '').trim();
        const email = String(profile.email || profile.EMAIL || profile.username || profile.USERNAME || '').trim();
        const deptIds = Array.isArray(deptIdsRaw) ? deptIdsRaw.map((value) => String(value).trim()).filter(Boolean) : [];
        const departments = Array.isArray(deptsRaw) ? deptsRaw.map((value) => String(value).trim()).filter(Boolean) : [];
        if (!cancelled) setProfileJobTitle(title);
        if (!cancelled) setProfileDepartmentId(deptId);
        if (!cancelled) setProfileDepartmentName(deptName);
        if (!cancelled) setProfileDepartmentIds(deptIds);
        if (!cancelled) setProfileDepartments(departments);
        if (!cancelled) setProfileEmail(email);
        if (!cancelled) setProfileHasFireDepartmentAccess(!!(profile.hasFireDepartmentAccess ?? profile.HAS_FIRE_DEPARTMENT_ACCESS));
        if (!cancelled) setProfileHasNonFireDepartmentAccess(!!(profile.hasNonFireDepartmentAccess ?? profile.HAS_NON_FIRE_DEPARTMENT_ACCESS));
      } catch (e) {
        if (!cancelled) setProfileJobTitle('');
        if (!cancelled) setProfileDepartmentId('');
        if (!cancelled) setProfileDepartmentName('');
        if (!cancelled) setProfileDepartmentIds([]);
        if (!cancelled) setProfileDepartments([]);
        if (!cancelled) setProfileEmail('');
        if (!cancelled) setProfileHasFireDepartmentAccess(false);
        if (!cancelled) setProfileHasNonFireDepartmentAccess(false);
      }
    };
    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const allDepartmentIds = useMemo(() => {
    const values = [
      ...profileDepartmentIds,
      ...(Array.isArray(user?.departmentIds) ? user.departmentIds : []),
      ...(Array.isArray(user?.DEPARTMENT_IDS) ? user.DEPARTMENT_IDS : []),
      profileDepartmentId,
      user?.departmentId,
      user?.DEPARTMENT_ID,
      user?.department_id
    ];

    return [...new Set(values.filter((value) => value !== undefined && value !== null && String(value).trim() !== '').map((value) => String(value).trim()))];
  }, [profileDepartmentId, profileDepartmentIds, user]);

  const allDepartments = useMemo(() => {
    const values = [
      ...profileDepartments,
      ...(Array.isArray(user?.departments) ? user.departments : []),
      ...(Array.isArray(user?.DEPARTMENTS) ? user.DEPARTMENTS : []),
      profileDepartmentName,
      user?.department,
      user?.DEPARTMENT
    ];

    return [...new Set(values.filter((value) => value !== undefined && value !== null && String(value).trim() !== '').map((value) => String(value).trim()))];
  }, [profileDepartmentName, profileDepartments, user]);

  const isFireDepartmentMember = useMemo(() => {
    const hasFireDepartmentId = allDepartmentIds.some((value) => Number(String(value).trim()) === 6);
    if (hasFireDepartmentId) {
      return true;
    }

    return allDepartments.some((departmentName) => {
      const normalizedDepartmentName = String(departmentName).trim().toLowerCase();
      return normalizedDepartmentName === 'fire department' || normalizedDepartmentName === 'fire' || normalizedDepartmentName.includes('fire');
    });
  }, [allDepartmentIds, allDepartments]);

  const isVolunteerFirefighter = useMemo(() => {
    const title = String(
      profileJobTitle || user?.jobTitle || user?.TITLE || user?.title || ''
    )
      .trim();
    const normalized = title.toLowerCase().replace(/[\s_-]+/g, '');
    return normalized === 'volunteerfirefighter';
  }, [profileJobTitle, user]);

  const hasNonFireDepartmentMembership = useMemo(() => {
    if (allDepartmentIds.some((value) => Number(String(value).trim()) !== 6)) {
      return true;
    }

    return allDepartments.some((departmentName) => {
      const normalizedDepartmentName = String(departmentName).trim().toLowerCase();
      return normalizedDepartmentName && normalizedDepartmentName !== 'fire department' && normalizedDepartmentName !== 'fire' && !normalizedDepartmentName.includes('fire');
    });
  }, [allDepartmentIds, allDepartments]);

  const normalizedEmail = useMemo(() => {
    return getNormalizedUserEmail(user) || String(profileEmail || '').trim().toLowerCase();
  }, [profileEmail, user]);

  const canAccessWcbDashboard = useMemo(() => {
    return isAuthenticated && hasWcbDashboardAccessEmail(normalizedEmail);
  }, [isAuthenticated, normalizedEmail]);

  const canAccessMaintenanceLog = useMemo(() => {
    return isAuthenticated && hasMaintenanceLogAccessEmail(normalizedEmail);
  }, [isAuthenticated, normalizedEmail]);

  const isFireAndNonFireOverride = useMemo(() => {
    if (!isAuthenticated) return false;
    return hasAllModuleAccessEmail(normalizedEmail);
  }, [isAuthenticated, normalizedEmail]);

  const hasFireDepartmentAccess =
    isVolunteerFirefighter ||
    isFireDepartmentMember ||
    profileHasFireDepartmentAccess ||
    !!(user?.hasFireDepartmentAccess ?? user?.HAS_FIRE_DEPARTMENT_ACCESS) ||
    hasExplicitFireAccessEmail(normalizedEmail);

  const hasNonFireDepartmentAccess =
    hasNonFireDepartmentMembership ||
    profileHasNonFireDepartmentAccess ||
    !!(user?.hasNonFireDepartmentAccess ?? user?.HAS_NON_FIRE_DEPARTMENT_ACCESS) ||
    (!hasFireDepartmentAccess && isAuthenticated);

  const showFireDepartmentLinks = isAuthenticated && (hasFireDepartmentAccess || isFireAndNonFireOverride);
  const showNonFireLinks = isAuthenticated && (hasNonFireDepartmentAccess || isFireAndNonFireOverride);
  const showCommitteeMeetingLinks = useMemo(() => hasCommitteeMeetingAccess({
    isAuthenticated,
    user,
    profile: {
      jobTitle: profileJobTitle,
      departmentId: profileDepartmentId,
      department: profileDepartmentName,
      departmentIds: profileDepartmentIds,
      departments: profileDepartments,
      hasFireDepartmentAccess: profileHasFireDepartmentAccess,
      hasNonFireDepartmentAccess: profileHasNonFireDepartmentAccess
    }
  }), [
    isAuthenticated,
    profileDepartmentId,
    profileDepartmentIds,
    profileDepartmentName,
    profileDepartments,
    profileHasFireDepartmentAccess,
    profileHasNonFireDepartmentAccess,
    profileJobTitle,
    user
  ]);

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      console.log('User logged out');
      window.location.href = getAppPath('/login');
    } catch (error) {
      console.error('Logout error:', error.message || error);
    }
  };

  const menuItems = (
    <List>
      {showNonFireLinks && (
        <>
          <ListItem button component={Link} to="/acknowledgments">
            <ListItemText primary="Pre-Job Hazard Assessments" />
          </ListItem>
          <ListItem button component={Link} to="/formal-hazard-assessment">
            <ListItemText primary="Formal Hazard Assessment (Safety)" />
          </ListItem>
	         <ListItem button component={Link} to="/hazard-assessment-history">
              <ListItemText primary="Hazard Assessment History" />
            </ListItem>
          <ListItem button component={Link} to="/incident-reports">
            <ListItemText primary="Incident Reporting / Near Misses" />
          </ListItem>
          <ListItem button component={Link} to="/incident-investigations">
            <ListItemText primary="Incident Investigations" />
          </ListItem>
          <ListItem button component={Link} to="/vehicle-inspection">
            <ListItemText primary="Vehicle Inspections" />
          </ListItem>
          {canAccessMaintenanceLog && (
          <ListItem button component={Link} to="/maintenance-log">
            <ListItemText primary="Maintenance Log" />
          </ListItem>
          )}
          <ListItem button component={Link} to="/toolbox-meetings">
            <ListItemText primary="Toolbox Meetings" />
          </ListItem>
          <ListItem button component={Link} to="/training-module">
            <ListItemText primary="Training Module" />
          </ListItem>
          <ListItem button component={Link} to="/inspection-app">
            <ListItemText primary="Facility Inspections" />
          </ListItem>
          <ListItem button component={Link} to="/inspection-history">
            <ListItemText primary="Inspection History" />
          </ListItem>
          {showCommitteeMeetingLinks && (
          <ListItem button component={Link} to="/committee-meeting">
            <ListItemText primary="Committee Meetings" />
          </ListItem>
          )}
          {showCommitteeMeetingLinks && (
          <ListItem button component={Link} to="/committee-meetings">
            <ListItemText primary="Committee Analytics" />
          </ListItem>
          )}
        </>
      )}
      {isAuthenticated && (
        <ListItem button component={Link} to="/contractor-prequalification">
          <ListItemText primary="Contractor Pre-qualification" />
        </ListItem>
      )}
      
      {/* Fire Department Section */}
      {showFireDepartmentLinks && (
        <>
          <ListItem sx={{ backgroundColor: '#dc2626', mt: 2 }}>
            <ListItemText 
              primary="Fire Department" 
              primaryTypographyProps={{ fontWeight: 'bold', color: 'white' }} 
            />
          </ListItem>
          <ListItem button component={Link} to="/fire-training-flha">
            <ListItemText primary="Training FLHA" />
          </ListItem>
          <ListItem button component={Link} to="/fire-training-flha-history">
            <ListItemText primary="FLHA History" />
          </ListItem>
          <ListItem button component={Link} to="/fire-incident-reports">
            <ListItemText primary="Incident Reporting" />
          </ListItem>
          <ListItem button component={Link} to="/fire-incident-investigations">
            <ListItemText primary="Incident Investigations" />
          </ListItem>
        </>
      )}
      
      {isAuthenticated && (
        <>
          {showNonFireLinks && allowedPages.includes('/hazard-forms') && (
            <ListItem button component={Link} to="/hazard-forms">
              <ListItemText primary="Hazard Forms" />
            </ListItem>
          )}
          {showNonFireLinks && allowedPages.includes('/dashboard') && (
            <ListItem button component={Link} to="/dashboard">
              <ListItemText primary="Dashboard" />
            </ListItem>
          )}
          {showNonFireLinks && allowedPages.includes('/employee-dashboard') && (
            <ListItem button component={Link} to="/employee-dashboard">
              <ListItemText primary="Employee Management" />
            </ListItem>
          )}
          {showNonFireLinks && canAccessWcbDashboard && (
            <ListItem button component={Link} to="/wcb-dashboard">
              <ListItemText primary="WCB / Insurance Dashboard" />
            </ListItem>
          )}
        </>
      )}
      {!isAuthenticated && (
        <>
          <ListItem button component={Link} to="/login">
            <ListItemText primary="Login" />
          </ListItem>
          <ListItem button component={Link} to="/register">
            <ListItemText primary="Register" />
          </ListItem>
        </>
      )}
      {isAuthenticated && (
        <>
          <ListItem button onClick={handleLogout}>
            <ListItemText primary="Logout" />
          </ListItem>
        </>
      )}
    </List>
  );

  return (
    <Box sx={{ flexGrow: 0 }}>
      <AppBar position="static">
        <Toolbar sx={{ minHeight: 64, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Left: Hamburger menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Center: Logo */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
                fontSize: { xs: '0.72rem', sm: '0.85rem' },
                lineHeight: 1.2,
                whiteSpace: 'nowrap'
              }}
            >
              <Box component="span" sx={{ mr: 0.5 }}>
                Need help? Contact:
              </Box>
              <Box
                component="a"
                href="mailto:support@3p3.ca"
                sx={{
                  color: 'inherit',
                  textDecoration: 'underline',
                  whiteSpace: 'nowrap'
                }}
              >
                support@3p3.ca
              </Box>
            </Box>
            {branding.showTextLogo ? (
              <Box
                component="span"
                sx={{
                  border: '1px solid rgba(255,255,255,0.65)',
                  borderRadius: '6px',
                  px: 1.5,
                  py: 0.75,
                  fontWeight: 700,
                  letterSpacing: 0,
                  lineHeight: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                {branding.name}
              </Box>
            ) : (
              <img
                src={branding.logo}
                alt={branding.logoAlt}
                style={{ height: 48, objectFit: 'contain', display: 'block' }}
              />
            )}
          </Box>

          {/* Right: User controls */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minWidth: 0 }}>
            {!isAuthenticated && (
              <Button color="inherit" component={Link} to="/login">
                Login
              </Button>
            )}
            {isAuthenticated && (
              <>
                <Tooltip title="Open settings">
                  <IconButton
                    onClick={handleOpenUserMenu}
                    sx={{ p: 0, ml: 2 }}
                    ref={avatarButtonRef}
                  >
                    <Avatar
                      alt={user?.username}
                      src="/static/images/avatar/1.jpg"
                    />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
          {/* Place Menu here, NOT inside the Box */}
          <Menu
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center', // Center under avatar
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center', // Center under avatar
            }}
          >
            <MenuItem component={Link} to="/profile" onClick={handleCloseUserMenu}>
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{ width: 250 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          {menuItems}
        </Box>
      </Drawer>
    </Box>
  );
};

export default Header;